import React, { useEffect, useMemo, useState } from "react";
import {
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import axiosInstance from "pages/Utility/axiosInstance";
import Breadcrumb from "components/Common/Breadcrumb2";
import { showError, showSuccess } from "helpers/notification_helper";
import { dangerIconStyle } from "styles/iconStyle";
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { getEncryptedLocal } from "pages/Utility/cookieUtils";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { useRef } from "react";
import TableContainer from "components/Common/DataTableContainer";
import { t } from "i18next";

/* ---------- VALIDATION ---------- */
const getValidationSchema = (isEdit) =>
  Yup.object({
    collection: Yup.object().nullable().required("Collection is required"),
    image: isEdit ? Yup.string() : Yup.string().required("Image is required"),
  });

const CollectionImageList = () => {
  const [collections, setCollections] = useState([]);
  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Modal state
  const [itemToDelete, setItemToDelete] = useState(null); // Item to delete
  const permissions = getEncryptedLocal("permissions") || [];
  const canAddImage = permissions.some(
    (item) => item?.permission_name === "Collection Image Add"
  );
  const canEditImage = permissions.some(
    (item) => item?.permission_name === "Collection Image Edit"
  );
  const canDeleteImage = permissions.some(
    (item) => item?.permission_name === "Collection Image Delete"
  );

  const isEditMode = Boolean(editingId);

  /* ---------- FETCH ---------- */
  const fetchData = async () => {
    setLoading(true);
    try {
      const [cRes, iRes] = await Promise.all([
        axiosInstance.get("V1/collections"),
        axiosInstance.get("V1/collections/collection-images"),
      ]);
      setCollections(cRes.data.data || []);
      setImages(iRes.data.data || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------- EDIT ---------- */
  const handleEdit = async (id) => {
    try {
      const res = await axiosInstance.get(
        `V1/collections/collection-images/${id}`
      );
      const img = res.data.data;

      setEditingId(id);
      setPreview(`${process.env.REACT_APP_BASE_IMAGE_URL}${img.image}`);
      setEditingValues({
        collection: {
          label: collections.find((c) => c.id === img.collectionId)?.title,
          value: img.collectionId,
        },
        image: "", // optional, only for new upload
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      showError(err.message);
    }
  };

  /* ---------- DELETE ---------- */
  const handleDelete = async () => {
    try {
      await axiosInstance.delete(
        `V1/collections/collection-images/${itemToDelete}`
      );
      showSuccess("Image deleted");
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      showError(err.message);
      setIsDeleteModalOpen(false);
    }
  };

  /* ---------- CANCELL ---------- */
  const handleCancel = (resetForm, setValues) => {
    setEditingId(null);
    setEditingValues(null);
    setPreview("");
    setValues({ collection: null, image: "" });

    resetForm(); // Reset the form fields
  };

  const columns = useMemo(
    () => [
      {
        header: "collection",
        accessorKey: "collection_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          return (
            <img
              src={`${process.env.REACT_APP_BASE_IMAGE_URL}${row.original.image}`}
              width={80}
            />
          )
        }
      },
      ...((canEditImage || canDeleteImage) ? [{
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="actions d-flex align-items-center gap-2">
              {canEditImage && (
                <Button
                  color="primary"
                  className="me-2"
                  onClick={() => handleEdit(row.original._id)}
                >
                  <FaRegEdit size={18} />
                </Button>
              )}
              {canDeleteImage && (
                <Button
                  style={dangerIconStyle}
                  onClick={() => {
                    setItemToDelete(row.original._id);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  <MdDelete size={18} />
                </Button>
              )}
            </div>
          );
        },
      },
      ] : []),
    ],
    [canDeleteImage, canEditImage,collections]
  );

  return (
    <div className="page-content">
      <Breadcrumb
        title="Collection Images"
        breadcrumbItems={[
          { title: "Dashboard", link: "/dashboard" },
          { title: "Collection Images", link: "#" },
        ]}
      />

      {(canAddImage || canEditImage) && (
        <Formik
          enableReinitialize
          initialValues={{
            collection: editingValues?.collection || null,
            image: editingValues?.image || "",
          }}
          validationSchema={getValidationSchema(isEditMode)}
          onSubmit={async (values, { resetForm }) => {
            const payload = {
              collectionId: values.collection.value,
              collection_name: values.collection.label,
              image: values.image || preview, // use existing image if no new upload
            };
            try {
              if (isEditMode) {
                await axiosInstance.put(
                  `V1/collections/collection-images/${editingId}`,
                  payload
                );
                showSuccess("Collection image updated");
              } else {
                await axiosInstance.post(
                  "V1/collections/collection-images",
                  payload
                );
                showSuccess("Collection image added");
              }
              resetForm();
              setEditingId(null);
              setEditingValues(null);
              setPreview("");
              fetchData();
            } catch (err) {
              showError(err.response.data.message);
            }
          }}
        >
          {({ errors, touched, setFieldValue, setValues, resetForm, values }) => (
            <FormikForm
              style={{
                backgroundColor: "white",
                padding: "20px 10px",
                borderRadius: "8px",
              }}
            >
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Collection *</Label>
                    <Select
                      options={collections.map((c) => ({
                        label: c.title,
                        value: c.id,
                      }))}
                      name="collection"
                      value={values?.collection || null}
                      onChange={(val) => setFieldValue("collection", val)}
                    />
                    {errors.collection && touched.collection && (
                      <div className="text-danger">{errors.collection}</div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={4}>
                  <FormGroup>
                    <Label>Image *</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      style={{
                        borderColor: "hsl(0, 0%, 80%)",
                        minHeight: "38px",
                      }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setPreview(reader.result);
                          setFieldValue("image", reader.result);
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    {preview && (
                      <img src={preview} width={100} className="mt-2" />
                    )}
                    {errors.image && touched.image && (
                      <div className="text-danger">{errors.image}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Button color="primary" type="submit">
                {isEditMode ? "Update" : "Save"}
              </Button>

              {(
                <Button
                  className="ms-2"
                  onClick={() => handleCancel(resetForm, setValues)}
                >
                  Cancel
                </Button>
              )}
            </FormikForm>
          )}
        </Formik>
      )}

    <h4 className="mt-5 ms-3">Collection Images</h4>

      <div className="">
        <div className="container-fluid">
          <TableContainer
            loading={loading}
            columns={columns}
            data={images || []}
            isGlobalFilter={true}
            isPagination={true}
            isDowload={true}
            SearchPlaceholder={t("Search")}
            pagination="pagination"
            docName="Roles"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isVisible={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)} // Close modal on cancel
        onOk={handleDelete} // Handle delete on confirmation
        title="Confirm Deletion"
        content="Are you sure you want to delete this image?"
      />
    </div>
  );
};

export default CollectionImageList;
